import { Checkbox } from 'antd';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  EmptyText,
  EmptyWrapper,
  FileIconWrapper,
  FileNameWrapper,
  FilesWrapper,
  FileWrapper,
  ItemWrapper,
  LoadingWrapper,
  SelectAllWrapper,
  Wrapper
} from './SectionFilesSelector.styles';

import { Icon } from '@/components/Icon';
import { LoadingCircleSpinner } from '@/components/Loaders';
import { useSectionFiles } from '@/store';

type Props = {
  value?: string[];
  onChange?: (value: string[]) => void;
  sectionId?: string;
};

export const SectionFilesSelector: React.FC<Props> = ({ value, onChange, sectionId }) => {
  const { sectionFiles, isLoading } = useSectionFiles(sectionId!);
  const { t } = useTranslation('ManagerQuizzesTranslations');

  const areAllFilesSelected = useMemo(
    () => value?.length === sectionFiles?.length,
    [value, sectionFiles]
  );

  const handleOpenFile = (fileUrl: string) => {
    window.open(fileUrl, '_blank');
  };

  const handleSelectAll = () => {
    if (areAllFilesSelected) onChange?.([]);
    else onChange?.(sectionFiles?.map(file => file._id) || []);
  };

  useEffect(() => {
    if (sectionFiles) onChange?.(sectionFiles.map(file => file._id));
  }, [sectionFiles]);

  return (
    <Wrapper>
      {isLoading ? (
        <LoadingWrapper>
          <LoadingCircleSpinner />
        </LoadingWrapper>
      ) : sectionFiles?.length ? (
        <FilesWrapper>
          <SelectAllWrapper>
            <Checkbox checked={areAllFilesSelected} onChange={handleSelectAll}>
              {t('SELECT_ALL')}
            </Checkbox>
          </SelectAllWrapper>
          <Checkbox.Group value={value} onChange={onChange}>
            {sectionFiles?.map((file, index) => {
              const isLast = index === sectionFiles.length - 1;
              return (
                <ItemWrapper key={file._id} $isLast={isLast}>
                  <Checkbox value={file._id}>
                    <FileWrapper onClick={() => handleOpenFile(file.url)}>
                      <FileIconWrapper>
                        <Icon i="DocumentPdf" />
                      </FileIconWrapper>
                      <FileNameWrapper>{file?.name || `${file._id}`}</FileNameWrapper>
                    </FileWrapper>
                  </Checkbox>
                </ItemWrapper>
              );
            })}
          </Checkbox.Group>
        </FilesWrapper>
      ) : (
        <EmptyWrapper>
          <Icon i="DocumentPdf" />
          <EmptyText>{t('NO_FILES')}</EmptyText>
        </EmptyWrapper>
      )}
    </Wrapper>
  );
};
